import {
  ArrowLeft,
  ArrowLeft2,
  ArrowRight2,
  SearchNormal,
  Sort,
} from "iconsax-react";
import { SquareArrowOutUpRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import instance, { facUrl } from "../../../axios/instance";
import { Link, useNavigate, useRevalidator } from "react-router-dom";
import { getToken } from "../../../utils/token";
import { toast } from "react-toastify";
import { PaginationButton } from "../../PaginationButton";

export const MedSpaConsultations = ({ onClose, facility, currentSpa }) => {
  const {
    code,
    total,
    pending_total: pendingTotal,
    name,
    consultations,
  } = currentSpa;
  const [consultationList, setConsultationList] = useState(
    [...consultations].map((_, index) => ({ ..._, index: index + 1 }))
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [loading, setLoading] = useState("");
  const [sorted, setSorted] = useState("");
  const [filteredList, setFilteredList] = useState(
    consultationList.filter((_, index) => index + 1 <= 10)
  );
  const revalidator = useRevalidator();

  const handleSearch = (e) => {
    e.preventDefault();

    const query = searchQuery.toLowerCase();
    setFilteredList(() => {
      return consultationList
        .filter((consultation) => {
          const treatment = consultation.treatment;
          if (!treatment) return false;
          const id = `${consultation.id}`;
          const tId = `${treatment.id}`;
          const title = treatment.title?.toLowerCase().includes(query);
          const patientName = consultation.patient_name
            ?.toLowerCase()
            ?.includes(query);
          const doctorName = consultation?.doctor_name
            ?.toLowerCase()
            ?.includes(query);

          return (
            id === query ||
            tId === query ||
            title ||
            patientName ||
            doctorName
          );
        })
        .filter((_, index) => index + 1 <= 10);
    });
  };

  useEffect(() => {
    setConsultationList(
      currentSpa.consultations.map((_, index) => ({ ..._, index: index + 1 }))
    );
  }, [currentSpa]);

  useEffect(() => {
    setFilteredList(() =>
      consultationList.filter(
        (_, index) =>
          index + 1 > 10 * (pageIndex - 1) && index + 1 <= 10 * pageIndex
      )
    );
  }, [pageIndex, consultationList]);

  const handleSort = (def) => {
    switch (def) {
      case "index": {
        setPageIndex(1);
        if (sorted !== "index") {
          setSorted("index");
        } else {
          setSorted("");
        }
        consultationList.reverse();
        setConsultationList(consultationList);
        setFilteredList((items) =>
          consultationList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "title": {
        setPageIndex(1);
        if (sorted === "title") {
          setSorted("");
          consultationList.sort((a, b) => a.index - b.index);
        } else {
          setSorted("title");
          consultationList.sort((a, b) => {
            if (a.title < b.title) return -1;
            if (a.title > b.title) return 1;
            return 0;
          });
        }
        setFilteredList((items) =>
          consultationList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "patient": {
        setPageIndex(1);
        if (sorted === "patient") {
          setSorted("");
          consultationList.sort((a, b) => a.index - b.index);
        } else {
          setSorted("patient");
          consultationList.sort((a, b) => {
            if (a.patient.lastname < b.patient.lastname) return -1;
            if (a.patient.lastname > b.patient.lastname) return 1;
            return 0;
          });
        }
        setFilteredList((items) =>
          consultationList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "amount": {
        setPageIndex(1);
        if (sorted === "amount") {
          setSorted("");
          consultationList.sort((a, b) => a.index - b.index);
        } else {
          setSorted("amount");
          consultationList.sort((a, b) => {
            if (+a.amount < +b.amount) return -1;
            if (+a.amount > +b.amount) return 1;
            return 0;
          });
        }
        setFilteredList((items) =>
          consultationList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "status": {
        setPageIndex(1);
        if (sorted === "status") {
          setSorted("");
          consultationList.sort((a, b) => a.index - b.index);
        } else {
          setSorted("status");
          consultationList.sort((a, b) => {
            if (a.meeting_status < b.meeting_status) return -1;
            if (a.meeting_status > b.meeting_status) return 1;
            return 0;
          });
        }
        setFilteredList((items) =>
          consultationList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "payout-status": {
        setPageIndex(1);
        if (sorted === "payout-status") {
          setSorted("");
          consultationList.sort((a, b) => a.index - b.index);
        } else {
          setSorted("payout-status");
          consultationList.sort((a, b) => {
            if (a.payout_status < b.payout_status) return -1;
            if (a.payout_status > b.payout_status) return 1;
            return 0;
          });
        }
        setFilteredList((items) =>
          consultationList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "date": {
        setPageIndex(1);
        if (sorted === "date") {
          setSorted("");
          consultationList.sort((a, b) => a.index - b.index);
        } else {
          setSorted("date");
          consultationList.sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );
        }
        setFilteredList((items) =>
          consultationList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
    }
  };

  const token = getToken();
  const markAsPaid = (consultation) => {
    setLoading(consultation.id);
    instance
      .put(
        "/doctor/med-spas/update-payout",
        {
          conId: consultation.id,
          spa: code,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        revalidator.revalidate();
        toast.success("Marked as paid");
        setLoading("");
      })
      .catch((error) => {
        toast.error(
          error.response ? error?.response?.data?.message : error.message
        );
        setLoading("");
      });
  };

  function handleCopy(text) {
    navigator.clipboard.writeText(text);
    toast.success("Website link copied");
  }

  const link = `${facUrl(facility)}?ref=${code}`;

  return (
    <div className="absolute inset-0 px-7 py-5 bg-white">
      <div className="flex justify-between items-center mb-5">
        <div className="flex gap-x-2 items-center">
          <ArrowLeft size={20} onClick={onClose} className="cursor-pointer" />
          <span className="text-lg font-medium">{name}</span>
        </div>
        <div>
          <div className="text-base mb-1 font-medium text-gray-600">
            Payout pending: ${pendingTotal.toLocaleString()}
          </div>
          <div className="text-base text-gray-600">
            Total: ${total.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="my-8">
        <div className="font-medium text-base text-gray-600 mb-2">URL:</div>
        <div className="grid grid-cols-[auto_100px_100px] gap-2">
          <input
            className="bg-gray-100 rounded-md p-3 text-gray-500 text-[14px]"
            disabled
            value={link}
          />
          <div className="flex gap-2">
            <button
              onClick={() => handleCopy(link)}
              className="px-6 border border-primary bg-primary text-white text-[14px] font-medium rounded-md"
            >
              Copy
            </button>
            <Link
              to={link}
              target="_blank"
              className="px-6 flex items-center gap-2 border border-primary bg-white text-primary text-[14px] font-medium rounded-md"
            >
              Open <SquareArrowOutUpRight />
            </Link>
          </div>
        </div>
      </div>

      <h2 className="text-lg font-medium">Consultations</h2>
      <div className="border bg-white rounded-md overflow-x-scroll scrollbar-hide my-4">
        <div className="flex justify-between p-2 items-center">
          <form className="relative" onSubmit={handleSearch}>
            <input
              className="outline-none h-[40px] w-[300px] border rounded-md p-2 placeholder:font-normal placeholder:text-[14px] text-sm"
              placeholder="Search consultation"
              name="query"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 right-[1px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
              type="submit"
            >
              <SearchNormal size={15} />
            </button>
          </form>

          <div className="flex gap-2 items-center">
            <PaginationButton
              item={<ArrowLeft2 size={16} />}
              disabled={pageIndex <= 1}
              handleClick={() => setPageIndex(pageIndex - 1)}
            />
            <span className="font-light text-xs">Page {pageIndex}</span>
            <PaginationButton
              item={<ArrowRight2 size={16} />}
              disabled={pageIndex >= Math.ceil(consultationList.length / 10)}
              handleClick={() => setPageIndex((index) => index + 1)}
            />
          </div>
        </div>
        <div className="max-h-[70vh] overflow-y-scroll scrollbar-hide">
          <table className="w-full relative whitespace-nowrap">
            <thead>
              <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("index")}
                >
                  #{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "index" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4">ID</td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("title")}
                >
                  Title{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "title" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("patient")}
                >
                  Patient{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "patient" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("amount")}
                >
                  Amount{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "amount" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("payout-status")}
                >
                  Payout Status{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "payout-status" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("status")}
                >
                  Status{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "status" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4" onClick={() => handleSort("date")}>
                  Date Created{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "date" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td></td>
              </tr>
            </thead>

            <tbody>
              {filteredList.length < 1 ? (
                <tr>
                  <td className="px-6 py-4 font-normal" colSpan={6}>
                    This referral does not have any consultation yet
                  </td>
                </tr>
              ) : (
                filteredList.map((consultation, index) => (
                  <tr
                    key={consultation.id}
                    className="h-14 text-[14px] font-normal border-b"
                  >
                    <td className="px-6 py-4">{consultation.index}</td>
                    <td className="px-6 py-4">
                      <Link
                        to={`/dashboard/telemedicine/consultations/${consultation.id}`}
                        className="underline text-primary"
                      >
                        {consultation.id}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      {consultation.treatment?.title || "N/A"}
                    </td>
                    <td className="px-6 py-4">{consultation.patient_name}</td>
                    <td className="px-6 py-4 capitalize">
                      ${+consultation.amount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 capitalize">
                      {consultation.payout_status}
                    </td>
                    <td className="px-6 py-4 capitalize">
                      {consultation.meeting_status}
                    </td>
                    <td className="px-6 py-4">
                      {new Date(consultation.created_at).toLocaleDateString()}
                    </td>
                    {consultation.payout_status?.toLowerCase() === "unpaid" ? (
                      <td
                        className="px-6 py-4 underline text-primary cursor-pointer w-fit"
                        onClick={() => markAsPaid(consultation)}
                      >
                        {loading === consultation.id
                          ? "Loading..."
                          : "Mark as paid"}
                      </td>
                    ) : (
                      <td></td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
